import { apiClient } from './client'
import type {
  PermissionListResponse,
  GrantPermissionRequest,
  UserPermission,
  PermissionRequest,
  PermissionRequestListResponse,
  CreatePermissionRequestPayload,
} from '@/types/permission'

export const permissionsApi = {
  /**
   * Get all permissions for a user
   */
  async getUserPermissions(userId: string) {
    const response = await apiClient.get<{
      success: boolean
      data: PermissionListResponse
    }>(`/users/${userId}/permissions/facilities`)
    return response.data
  },

  /**
   * Get permission for a specific facility
   */
  async getFacilityPermission(userId: string, facilityId: string) {
    const response = await apiClient.get<{ success: boolean; data: UserPermission }>(
      `/users/${userId}/permissions/facilities/${facilityId}`
    )
    return response.data
  },

  /**
   * Grant or update permissions for a facility
   */
  async setFacilityPermission(
    userId: string,
    facilityId: string,
    permissions: GrantPermissionRequest
  ) {
    const response = await apiClient.put<{ success: boolean; data: UserPermission }>(
      `/users/${userId}/permissions/facilities/${facilityId}`,
      permissions
    )
    return response.data
  },

  /**
   * Revoke all permissions for a facility
   */
  async revokeFacilityPermission(userId: string, facilityId: string, reason?: string) {
    const response = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/users/${userId}/permissions/facilities/${facilityId}/revoke`,
      { reason: reason || 'Revoked via X-Manager' }
    )
    return response.data
  },

  /**
   * Get all permission requests (with optional filters)
   */
  async getPermissionRequests(params?: {
    status?: string
    requesterId?: string
    targetUserId?: string
    page?: number
    pageSize?: number
  }) {
    const response = await apiClient.get<{
      success: boolean
      data: PermissionRequestListResponse
    }>('/permission-requests', { params })
    return response.data
  },

  /**
   * Get a specific permission request
   */
  async getPermissionRequest(id: string) {
    const response = await apiClient.get<{
      success: boolean
      data: PermissionRequest
    }>(`/permission-requests/${id}`)
    return response.data
  },

  /**
   * Create a new permission request
   */
  async createPermissionRequest(payload: CreatePermissionRequestPayload) {
    const response = await apiClient.post<{
      success: boolean
      data: { request: PermissionRequest; autoApproved: boolean }
    }>('/permission-requests', payload)
    return response.data
  },

  /**
   * Approve a permission request
   */
  async approvePermissionRequest(id: string) {
    const response = await apiClient.post<{
      success: boolean
      data: PermissionRequest
    }>(`/permission-requests/${id}/approve`)
    return response.data
  },

  /**
   * Reject a permission request
   */
  async rejectPermissionRequest(id: string, reason: string) {
    const response = await apiClient.post<{
      success: boolean
      data: PermissionRequest
    }>(`/permission-requests/${id}/reject`, { reason })
    return response.data
  },
}
